import { useQueryClient } from "@tanstack/react-query";
import {
  Autocomplete,
  CircularProgress,
  CloseIcon,
  DeleteIcon,
  GKButton,
  GKModal,
  Grid2,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "components";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { debounce } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import axiosInstance from "utils/axiosInstance";
import { GKModalProps } from "utils/commonInterface";
import { QueryKeys } from "utils/queryKey";

const AddNewStockModal = (props: GKModalProps) => {
  const { open, setOpen } = props;

  const { id } = useParams();

  const queryClient = useQueryClient();

  const [searchText, setSearchText] = useState("");

  const [debouncedSearch, setDebouncedSearch] = useState("");

  const debouncedSearchHandler: any = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearch(value);
      }, 500),
    [],
  );

  /** ========================================================================================================= */
  const companyListApi = useFetchQuery({
    key: [QueryKeys.COMPANY_NAMES, debouncedSearch],
    route: `/instruments/list/`,
    requestBody: {
      take: 300,
      skip: 0,
      search: debouncedSearch,
      inst_type: JSON.stringify([
        "EQ",
        "INDEX",
        "ETF",
        "BOND",
        "MF",
        "CASH",
        "PEQ",
        "GEQ",
        "FD",
        "AIF",
        "OPTIONS",
        "FUTURES",
      ]),
    },
  });

  /** ========================================================================================================= */
  const { mutate: handleStockSubmit, isLoading } = useMutation({
    mutationFn: (values: any) =>
      axiosInstance.patch(`/model_portfolio/${id}/stock_universe/`, {
        id: Number(id),
        allocItem: values?.stocks,
        isRecommendation: false,
      }),
    onSuccess: () => {
      queryClient?.refetchQueries(["STOKE_UNIVERSE"]);
      setOpen(false);
    },
  });

  /** ========================================================================================================= */
  const { control, watch, setValue, handleSubmit } = useForm({
    defaultValues: {
      stocks: [
        {
          isinCode: "",
          allocation: null,
          targetPrice: null,
          stopLoss: null,
          rationale: "",
          gridCode: "",
          companyName: "",
        },
      ],
    },
  });
  watch();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "stocks",
  });

  const handleFormSubmit = (values: any) => {
    handleStockSubmit(values);
  };

  useEffect(() => {
    debouncedSearchHandler(searchText);
    return () => {
      debouncedSearchHandler.cancel();
    };
  }, [searchText]);

  return (
    <GKModal
      modalTitle="Add Stocks"
      open={open}
      setOpen={setOpen}
      footer={
        <Grid2 container justifyContent={"flex-end"}>
          <GKButton
            type="submit"
            form="equity-basket-stock-form"
            variant="contained"
            size="medium"
            loading={isLoading}
          >
            {"Save"}
          </GKButton>
        </Grid2>
      }
      titleComponent={
        <GKButton
          onClick={() =>
            append({
              isinCode: "",
              allocation: null,
              targetPrice: null,
              stopLoss: null,
              rationale: "",
              gridCode: "",
              companyName: "",
            })
          }
          size="small"
          variant="outlined"
        >
          Add Stocks
        </GKButton>
      }
    >
      <Grid2 container>
        <Grid2 size={12}>
          <form
            id="equity-basket-stock-form"
            onSubmit={handleSubmit(handleFormSubmit)}
          >
            <Grid2
              container
              spacing={1}
              justifyContent={"space-between"}
              alignItems={"flex-end"}
            >
              <Grid2 size={12}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Stocks</TableCell>
                        <TableCell> </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {fields.map((item: any, index: number) => (
                        <TableRow key={index}>
                          <TableCell
                            width={"80%"}
                            style={{
                              padding: 6,
                            }}
                          >
                            <Controller
                              control={control}
                              name={`stocks.${index}.gridCode`}
                              rules={{
                                required: {
                                  value: true,
                                  message: "Please select a company",
                                },
                              }}
                              render={({ field, fieldState: { error } }) => {
                                return (
                                  <Autocomplete
                                    {...field}
                                    value={watch(`stocks.${index}.companyName`)}
                                    onChange={(_, val: any) => {
                                      field.onChange(val?.value);
                                      setValue(
                                        `stocks.${index}.gridCode`,
                                        val.gridCode,
                                      );
                                      setValue(
                                        `stocks.${index}.companyName`,
                                        val.name,
                                      );
                                      setDebouncedSearch("");
                                      setSearchText("");
                                    }}
                                    options={
                                      companyListApi?.data?.data?.data?.map(
                                        (item: any) => ({
                                          ...item,
                                          name: item?.companyName,
                                          value: item?.isin,
                                          gridCode: item?.gridCode,
                                        }),
                                      ) || []
                                    }
                                    getOptionLabel={(option: any) =>
                                      option?.name || option
                                    }
                                    loading={companyListApi?.isFetching}
                                    renderOption={(props, option: any) => (
                                      <li
                                        {...props}
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 4,
                                        }}
                                      >
                                        <Typography
                                          color={"var(--white)"}
                                          fontSize={8}
                                          style={{
                                            background:
                                              option?.instType === "EQ"
                                                ? "var(--secondary-900)"
                                                : "var(--orange-700)",
                                            padding: "2px 4px",
                                            borderRadius: 4,
                                          }}
                                        >
                                          {option?.instType}
                                        </Typography>
                                        <Typography whiteSpace={"nowrap"}>
                                          {option?.name}
                                        </Typography>
                                      </li>
                                    )}
                                    onInputChange={(event, newValue) => {
                                      setSearchText(newValue);
                                    }}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        placeholder={"Please select a company"}
                                        error={!!error}
                                        helperText={!!error && error?.message}
                                        InputLabelProps={{
                                          sx: {
                                            "& .MuiFormLabel-asterisk": {
                                              color: "red",
                                            },
                                          },
                                        }}
                                        InputProps={{
                                          ...params.InputProps,
                                          endAdornment: (
                                            <>
                                              {companyListApi?.isFetching && (
                                                <CircularProgress
                                                  color="inherit"
                                                  size={20}
                                                />
                                              )}
                                              {params.InputProps.endAdornment}
                                            </>
                                          ),
                                        }}
                                      />
                                    )}
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: 2,
                                      },
                                      "& .MuiChip-root": {
                                        borderRadius: "6px",
                                        margin: "0 3px",
                                      },
                                      "& .MuiFormHelperText-root": {
                                        marginLeft: 0,
                                        marginRight: 0,
                                      },
                                    }}
                                    onKeyDown={(e) => {
                                      if (
                                        e.key === "ArrowUp" ||
                                        e.key === "ArrowDown" ||
                                        e.key === "ArrowLeft" ||
                                        e.key === "ArrowRight" ||
                                        e.key === " "
                                      ) {
                                        e.stopPropagation();
                                      }
                                    }}
                                    slotProps={{
                                      chip: {
                                        deleteIcon: (
                                          <CloseIcon width={16} height={16} />
                                        ),
                                      },
                                      popper: {
                                        placement: "bottom",
                                      },
                                    }}
                                  />
                                );
                              }}
                            />
                          </TableCell>
                          <TableCell
                            align="right"
                            style={{
                              padding: 6,
                            }}
                          >
                            <IconButton
                              color="error"
                              className="outline"
                              onClick={() => {
                                remove(index);
                              }}
                            >
                              <DeleteIcon />{" "}
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid2>
            </Grid2>
          </form>
        </Grid2>
      </Grid2>
    </GKModal>
  );
};

export default AddNewStockModal;
